<script setup>
import { computed } from 'vue';

// Auditoria 2026-08-08 (G3): o funil já define critérios de qualificação em
// `settings.lead_scoring.qualification_criteria` — com id, nome, pontos,
// categoria, validação e teto de 100 pontos — e o card guarda quais foram
// atendidos em `qualification_checklist`, que alimenta o `qualification_score`.
// Havia endpoint, store e normalização; não havia nenhuma tela. O usuário não
// tinha como marcar um critério.
const props = defineProps({
  criteria: {
    type: Array,
    default: () => [],
  },
  checklist: {
    type: Object,
    default: () => ({}),
  },
  score: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const isChecked = criterion =>
  Boolean(props.checklist?.[criterion.id]?.checked);

// Cross-review Codex: o default de prop só cobre `undefined`. `settings` é
// JSONB opcional, então `criteria` pode chegar `null` — e `null.filter` derruba
// o painel inteiro.
const items = computed(() =>
  (Array.isArray(props.criteria) ? props.criteria : [])
    .filter(criterion => criterion?.id)
    .map(criterion => ({
      id: criterion.id,
      name: criterion.name || criterion.id,
      points: Number(criterion.points) || 0,
      category: criterion.category || null,
      checked: isChecked(criterion),
    }))
);

const completedCount = computed(
  () => items.value.filter(item => item.checked).length
);

const totalPoints = computed(() =>
  items.value.reduce((sum, item) => sum + item.points, 0)
);

// O score vem do backend (é ele quem soma e valida). Aqui só se desenha a
// barra; recalcular no cliente criaria uma segunda verdade.
const progress = computed(() => {
  if (totalPoints.value <= 0) return 0;
  return Math.min(100, Math.round((props.score / totalPoints.value) * 100));
});

// Cross-review Codex: com `@change`, o navegador já teria marcado a caixa
// enquanto o `checked` renderizado ainda vem do prop antigo — a tela mostra um
// estado que o servidor não confirmou. O `@click.prevent` mantém o DOM sob
// controle do prop: a marca só muda quando o dado muda.
const toggle = item => {
  if (props.disabled) return;

  // Manda o checklist inteiro: o endpoint substitui o mapa, e mandar só a
  // diferença apagaria os outros critérios.
  const next = { ...(props.checklist || {}) };
  next[item.id] = {
    ...(next[item.id] || {}),
    checked: !item.checked,
    points: item.points,
  };

  emit('update', next);
};
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
    <div class="mb-3 flex items-baseline justify-between gap-3">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t('PIPELINE.QUALIFICATION.CRITERIA_CHECKLIST') }}
      </h3>
      <span class="text-xs tabular-nums text-n-slate-11">
        {{ completedCount }}/{{ items.length }}
        {{ $t('PIPELINE.QUALIFICATION.CRITERIA_COMPLETED') }}
      </span>
    </div>

    <div v-if="items.length === 0" class="py-3 text-center">
      <p class="text-xs text-n-slate-11">
        {{ $t('PIPELINE.QUALIFICATION.NO_CRITERIA') }}
      </p>
      <p class="mt-1 text-xs text-n-slate-10">
        {{ $t('PIPELINE.QUALIFICATION.NO_CRITERIA_HINT') }}
      </p>
    </div>

    <template v-else>
      <div class="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-n-alpha-2">
        <div
          class="h-full rounded-full bg-n-brand transition-all"
          :style="{ width: `${progress}%` }"
        />
      </div>

      <ul class="flex flex-col gap-1">
        <li v-for="item in items" :key="item.id">
          <label
            class="flex cursor-pointer items-center gap-2 rounded px-1 py-1 !mb-0 hover:bg-n-alpha-1"
            :class="{ 'cursor-not-allowed opacity-60': disabled }"
          >
            <input
              type="checkbox"
              :checked="item.checked"
              :disabled="disabled"
              :data-testid="`qualification-${item.id}`"
              class="size-4 shrink-0 rounded border-n-weak accent-n-brand"
              @click.prevent="toggle(item)"
            />
            <span
              class="flex-1 text-xs"
              :class="item.checked ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{ item.name }}
            </span>
            <span class="text-xs tabular-nums text-n-slate-10">
              {{ item.points }}
            </span>
          </label>
        </li>
      </ul>

      <p class="mt-3 text-xs text-n-slate-11">
        {{ $t('PIPELINE.QUALIFICATION.TOTAL_SCORE') }}
        <span class="tabular-nums font-medium text-n-slate-12">
          {{ score }}/{{ totalPoints }}
        </span>
      </p>
    </template>
  </div>
</template>
